/**
 * Vendor archive / lookup helpers (mirrors customersRefresh for VendorManagement).
 */

const getStore = () => require("../index").default;

export const getVendorNestedStatus = (vendor) => {
  const raw =
    vendor?.organizations?.[0]?.status ||
    vendor?.individuals?.[0]?.status ||
    vendor?.status ||
    "Active";
  return String(raw || "Active");
};

export const isVendorArchived = (vendor) =>
  getVendorNestedStatus(vendor).toLowerCase() === "archived";

export const partitionVendorsByStatus = (list = []) => {
  const active_vendors = [];
  const archived_vendors = [];
  (list || []).forEach((vendor) => {
    if (isVendorArchived(vendor)) archived_vendors.push(vendor);
    else active_vendors.push(vendor);
  });
  return { active_vendors, archived_vendors };
};

export const findVendorInState = (vendorsState, id) => {
  if (id == null || String(id).trim().toLowerCase() === "null") return null;
  const normalizedId = String(id);
  const pools = [
    ...(vendorsState?.vendors || []),
    ...(vendorsState?.active_vendors || []),
    ...(vendorsState?.archived_vendors || []),
  ];
  return (
    pools.find(
      (vendor) =>
        String(vendor?.POID ?? vendor?.id ?? vendor?.poid) === normalizedId
    ) || null
  );
};

const vendorIdOf = (vendor) =>
  String(vendor?.POID ?? vendor?.id ?? vendor?.poid ?? "");

const withNestedStatus = (vendor, status) => {
  if (!vendor) return vendor;
  const next = { ...vendor, status: status === "Archived" ? "archived" : "active" };
  if (Array.isArray(vendor.organizations) && vendor.organizations.length > 0) {
    next.organizations = vendor.organizations.map((org, idx) =>
      idx === 0 ? { ...org, status } : org
    );
  }
  if (Array.isArray(vendor.individuals) && vendor.individuals.length > 0) {
    next.individuals = vendor.individuals.map((ind, idx) =>
      idx === 0 ? { ...ind, status } : ind
    );
  }
  return next;
};

/**
 * Build VendorManagement PUT body to set Active / Archived while preserving fields.
 */
export const buildVendorStatusPayload = (vendor, status = "Archived") => {
  const isOrg =
    vendor?.type === "Organization" ||
    vendor?.type === "organization" ||
    (Array.isArray(vendor?.organizations) && vendor.organizations.length > 0);

  if (isOrg) {
    const org = vendor.organizations?.[0] || {};
    return {
      status: status === "Archived" ? "archived" : "active",
      type: "Organization",
      organizations: [
        {
          tradingName: org.tradingName || vendor.tradingName || "",
          status,
          contactMediums: org.contactMediums || [
            { mediumCharacteristics: [{}] },
          ],
        },
      ],
    };
  }

  const ind = vendor.individuals?.[0] || {};
  return {
    status: status === "Archived" ? "archived" : "active",
    type: "Individual",
    individuals: [
      {
        gender: ind.gender || "Male",
        placeOfBirth: ind.placeOfBirth || "",
        countryOfBirth: ind.countryOfBirth || "",
        nationality: ind.nationality || "",
        maritalStatus: ind.maritalStatus || "Single",
        title: ind.title || "Mr.",
        givenName: ind.givenName || "",
        familyName: ind.familyName || "",
        middleName: ind.middleName || "",
        location: ind.location || "",
        fullName: ind.fullName || "",
        status,
        contactMediums: ind.contactMediums || [
          { mediumCharacteristics: [{}] },
        ],
      },
    ],
  };
};

export const upsertVendorInStore = (vendor) => {
  if (!vendor) return;
  const normalized = {
    ...vendor,
    POID: vendor.POID || vendor.id || vendor.poid,
    id: vendor.id || vendor.POID || vendor.poid,
  };
  if (!normalized.POID) return;
  const archived = isVendorArchived(normalized);
  getStore().dispatch({
    type: archived ? "UPSERT_ARCHIVED_VENDOR" : "UPSERT_ACTIVE_VENDOR",
    payload: withNestedStatus(
      normalized,
      archived ? "Archived" : getVendorNestedStatus(normalized) || "Active"
    ),
  });
};

export const applyVendorsToStore = (vendors) => {
  getStore().dispatch({
    type: "GET_VENDORS",
    vendors: Array.isArray(vendors) ? vendors : [],
  });
};

export { withNestedStatus, vendorIdOf };
